import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ActionItem } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { useOutreachTable } from "features/Influencer/ContactList/useOutreachTable";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
} from "./OutreachContacts";

export const PER_PAGE = 10;

export enum ActionItemOrderBy {
  priority = "priority",
  created_at = "created_at",
  last_gmail_message_date = "last_gmail_message_date",
}

interface TargetedCheckInsContextInterface {
  actionItem: ActionItem | null;
  setActionItem: React.Dispatch<SetStateAction<ActionItem | null>>;
  actionItems: ActionItem[];
  setActionItems: React.Dispatch<SetStateAction<ActionItem[]>>;
  fetchCount: () => void;
  fetchTargetedActionItems: (
    page: number,
    orderBy?: ActionItemOrderBy,
    refreshList?: boolean,
  ) => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  total: number;
  setTotal: Dispatch<SetStateAction<number>>;
  fetchLoading: boolean;
  fetchMoreLoading: boolean;
  fetchSingleItemLoading: boolean;
  setFetchSingleItemLoading: Dispatch<SetStateAction<boolean>>;
  fetchSingleActionItem: (actionItemId: number) => void;
  orderBy: ActionItemOrderBy | undefined;
  setOrderBy: Dispatch<SetStateAction<ActionItemOrderBy | undefined>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  actionItems: [],
  setActionItems: defaultContextMissingFunction,
  fetchCount: defaultContextMissingFunction,
  fetchTargetedActionItems: defaultContextMissingFunction,
  page: 1,
  setPage: defaultContextMissingFunction,
  total: 0,
  setTotal: defaultContextMissingFunction,
  fetchLoading: false,
  fetchMoreLoading: false,
  actionItem: null,
  setActionItem: defaultContextMissingFunction,
  fetchSingleItemLoading: false,
  setFetchSingleItemLoading: defaultContextMissingFunction,
  fetchSingleActionItem: defaultContextMissingFunction,
  orderBy: undefined,
  setOrderBy: defaultContextMissingFunction,
};

const TargetedCheckInsContext =
  createContext<TargetedCheckInsContextInterface>(defaultInterface);

interface TargetedCheckInsProviderProps {
  children: React.ReactNode;
}

const TargetedCheckInsProvider = ({
  children,
}: TargetedCheckInsProviderProps) => {
  const { setTotals } = useContext(OutreachContactsContext);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [fetchSingleItemLoading, setFetchSingleItemLoading] = useState(false);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionItem, setActionItem] = useState<ActionItem | null>(null);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<ActionItemOrderBy | undefined>();
  const navigate = useNavigate();
  const { isSmallScreen } = useOutreachTable();

  const fetchTargetedActionItems = async (
    page: number,
    orderBy?: ActionItemOrderBy,
    refreshList = true,
  ) => {
    if (refreshList) {
      setFetchLoading(true);
    } else {
      setFetchMoreLoading(true);
    }
    let url = `/api/organization/${currentOrg?.id}/action-items/targeted-check-ins?page=${page}&per_page=${PER_PAGE}`;
    if (orderBy) {
      url += `&order_by=${orderBy}`;
    }
    let cursor = null;
    if (!refreshList) {
      const lastActionItem = actionItems[actionItems.length - 1];
      if (lastActionItem) {
        cursor = lastActionItem
          ? [lastActionItem.createdAt, lastActionItem.id]
          : null;
      }
    }
    if (cursor) {
      url += `&cursor=${JSON.stringify(cursor)}`;
    }
    try {
      const res = await fetcherAuth(getToken, url);
      setActionItems((prev) => {
        if (refreshList) {
          return res.actionItems;
        } else {
          return [...prev, ...res.actionItems];
        }
      });
      setPage(page);

      // Redirect to first action item if not on email page
      if (!isPage("/email") && !isSmallScreen && res.actionItems[0]?.id) {
        if (actionItem && actionItem !== res.actionItems[0]?.id)
          setActionItem(null);
        navigate(
          `/${routes.inboxCheckins}/${res.actionItems[0]?.id}/contact/${res.actionItems?.[0]?.outreachContactId}/email/${res.actionItems?.[0].gmailThreadId}`,
        );
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
      setFetchMoreLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/targeted-check-ins-total`,
      );
      setTotal(res.total);
      setTotals((prev) => ({
        ...prev,
        [InboxSidebarOptionKey.checkins]: res.total,
      }));
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const fetchSingleActionItem = async (actionItemId: number) => {
    try {
      setFetchSingleItemLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItemId}`,
      );
      setActionItem(res.actionItem);
    } catch (error) {
      // Non-existing action item, failed silently
    } finally {
      setFetchSingleItemLoading(false);
    }
  };

  return (
    <TargetedCheckInsContext.Provider
      value={{
        actionItems,
        setActionItems,
        fetchCount,
        fetchTargetedActionItems,
        fetchLoading,
        fetchMoreLoading,
        page,
        setPage,
        total,
        setTotal,
        actionItem,
        setActionItem,
        fetchSingleItemLoading,
        setFetchSingleItemLoading,
        fetchSingleActionItem,
        orderBy,
        setOrderBy,
      }}
    >
      {children}
    </TargetedCheckInsContext.Provider>
  );
};

export { TargetedCheckInsProvider, TargetedCheckInsContext };
